import {
  AuditTrailFilterDateTimeValue,
  AuditTrailFilterMetadata,
  AuditTrailFilterStringRegEx,
} from '../components/AuditTrailPageControls/types';

/**
 * URL parameter management utilities for Audit Trail filters.
 * Pure functions for serialization/deserialization of filter states to/from URL search parameters.
 */

// Filter type utilities - each handles a specific filter data structure
export const getStringFilter = (searchParams: URLSearchParams, paramName: string): string | undefined => {
  return searchParams.get(paramName) || undefined;
};

export const setStringFilter = (searchParams: URLSearchParams, paramName: string, value: string | undefined) => {
  if (value) searchParams.set(paramName, value);
  else searchParams.delete(paramName);
};

export const getDateTimeFilter = (
  searchParams: URLSearchParams,
  dateStartParam: string,
  dateEndParam: string
): AuditTrailFilterDateTimeValue | undefined => {
  const dateStart = searchParams.get(dateStartParam) || undefined;
  const dateEnd = searchParams.get(dateEndParam) || undefined;

  if (dateStart || dateEnd) return { dateStart, dateEnd };

  return undefined;
};

export const setDateTimeFilter = (
  searchParams: URLSearchParams,
  dateStartParam: string,
  dateEndParam: string,
  value: AuditTrailFilterDateTimeValue | undefined
) => {
  searchParams.delete(dateStartParam);
  searchParams.delete(dateEndParam);

  if (value?.dateStart) searchParams.set(dateStartParam, value.dateStart);
  if (value?.dateEnd) searchParams.set(dateEndParam, value.dateEnd);
};

export const getRegexFilter = (
  searchParams: URLSearchParams,
  inputParam: string,
  regexParam: string
): AuditTrailFilterStringRegEx | undefined => {
  const input = searchParams.get(inputParam) || undefined;
  const isRegEx = searchParams.get(regexParam);

  if (input !== undefined || isRegEx !== null) {
    return {
      inputData: input,
      isRegEx: isRegEx === 'true',
    };
  }
  return undefined;
};

export const setRegexFilter = (
  searchParams: URLSearchParams,
  inputParam: string,
  regexParam: string,
  value: AuditTrailFilterStringRegEx | undefined
) => {
  searchParams.delete(inputParam);
  searchParams.delete(regexParam);

  if (value) {
    if (value.inputData !== undefined) searchParams.set(inputParam, value.inputData);
    if (value.isRegEx) searchParams.set(regexParam, 'true');
  }
};

export const getBooleanFilter = (searchParams: URLSearchParams, paramName: string): boolean => {
  return searchParams.get(paramName) === 'true';
};

export const setBooleanFilter = (searchParams: URLSearchParams, paramName: string, value: boolean) => {
  if (value) searchParams.set(paramName, 'true');
  else searchParams.delete(paramName);
};

export const getMetadataFilter = (
  searchParams: URLSearchParams,
  paramName: string
): AuditTrailFilterMetadata | undefined => {
  const metadataStr = searchParams.get(paramName);
  if (!metadataStr) return undefined;

  try {
    return JSON.parse(metadataStr);
  } catch (error) {
    return undefined;
  }
};

export const setMetadataFilter = (
  searchParams: URLSearchParams,
  paramName: string,
  value: AuditTrailFilterMetadata | undefined
) => {
  if (value && Object.keys(value).length > 0) {
    searchParams.set(paramName, JSON.stringify(value));
  } else {
    searchParams.delete(paramName);
  }
};

export const clearAllFilters = (searchParams: URLSearchParams, filterParams: string[]) => {
  filterParams.forEach((param) => searchParams.delete(param));
};
